import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  MenuItem,
  Collapse,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import PaidIcon from "@mui/icons-material/Paid";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";

import api from "../../../utils/api";
import { PermissionsContext } from "../../../utils/PermissionsContext";

// ---------- Helpers ----------
const fmtCurrency = (n) => {
  const v = Number(n ?? 0);
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

const fmtDateTime = (v) => {
  if (!v) return "N/A";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleString();
};

const toDateTimeLocalValue = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (x) => String(x).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

const fromDateTimeLocalValue = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

// ---------- Dialog ----------
function FineDialog({ open, mode, initial, onClose, onSave, isSaving }) {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setForm(initial);
    setErrors({});
  }, [initial, open]);

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const setField = (key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
    setErrors((p) => ({ ...p, [key]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.title?.trim()) next.title = "Title is required.";
    if (form.amount === "" || form.amount === null || Number.isNaN(Number(form.amount))) {
      next.amount = "Amount must be a number.";
    }
    if (Number(form.amount ?? 0) < 0) next.amount = "Amount can’t be negative.";

    if (form.is_recurring) {
      if (!form.recurrence_frequency) next.recurrence_frequency = "Frequency is required.";
      if (form.recurrence_interval === "" || Number.isNaN(Number(form.recurrence_interval))) {
        next.recurrence_interval = "Interval must be a number.";
      }
      if (Number(form.recurrence_interval ?? 1) < 1) {
        next.recurrence_interval = "Interval must be at least 1.";
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      ...form,
      amount: Number(form.amount ?? 0),
      recurrence_interval: form.is_recurring ? Number(form.recurrence_interval ?? 1) : null,
      recurrence_frequency: form.is_recurring ? (form.recurrence_frequency || null) : null,
      recurrence_end_date: form.is_recurring
        ? (fromDateTimeLocalValue(form.recurrence_end_date) || null)
        : null,
      effective_date: fromDateTimeLocalValue(form.effective_date) || null,
      is_recurring: !!form.is_recurring,
      is_active: !!form.is_active,
    };

    await onSave(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: "#223142" }}>
        {mode === "create" ? "Create Fine Rule" : `Edit Fine Rule #${initial?.id ?? ""}`}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Title"
            value={form.title || ""}
            onChange={(e) => setField("title", e.target.value)}
            error={!!errors.title}
            helperText={errors.title || " "}
            fullWidth
            autoFocus
            sx={textFieldSx}
          />

          <TextField
            label="Description"
            value={form.description || ""}
            onChange={(e) => setField("description", e.target.value)}
            multiline
            minRows={3}
            fullWidth
            sx={textFieldSx}
          />

          <TextField
            label="Amount"
            value={form.amount ?? ""}
            onChange={(e) => setField("amount", e.target.value)}
            error={!!errors.amount}
            helperText={errors.amount || " "}
            fullWidth
            inputProps={{ inputMode: "decimal" }}
            sx={textFieldSx}
          />

          <TextField
            label="Effective Date (first run)"
            type="datetime-local"
            value={toDateTimeLocalValue(form.effective_date)}
            onChange={(e) => setField("effective_date", e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={textFieldSx}
          />

          <Divider />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.is_recurring}
                  onChange={(e) => setField("is_recurring", e.target.checked)}
                />
              }
              label="Recurring fine"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.is_active}
                  onChange={(e) => setField("is_active", e.target.checked)}
                />
              }
              label="Active"
            />
          </Stack>

          {form.is_recurring ? (
            <Stack spacing={2}>
              <TextField
                select
                label="Frequency"
                value={form.recurrence_frequency || ""}
                onChange={(e) => setField("recurrence_frequency", e.target.value)}
                error={!!errors.recurrence_frequency}
                helperText={errors.recurrence_frequency || " "}
                fullWidth
                sx={textFieldSx}
              >
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </TextField>

              <TextField
                label="Interval"
                value={form.recurrence_interval ?? 1}
                onChange={(e) => setField("recurrence_interval", e.target.value)}
                error={!!errors.recurrence_interval}
                helperText={errors.recurrence_interval || "Example: 1 = every week/month"}
                fullWidth
                inputProps={{ inputMode: "numeric" }}
                sx={textFieldSx}
              />

              <TextField
                label="End Date (optional)"
                type="datetime-local"
                value={toDateTimeLocalValue(form.recurrence_end_date)}
                onChange={(e) => setField("recurrence_end_date", e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={textFieldSx}
              />
            </Stack>
          ) : null}

          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Member Number: <b>{form.member_number || "N/A"}</b>
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1.25 }}>
        <Button onClick={onClose} disabled={isSaving} variant="outlined" sx={secondaryButtonSx}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={isSaving} sx={primaryButtonSx}>
          {isSaving ? "Saving..." : mode === "create" ? "Create" : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------- Main ----------
export default function ViolationFinesTab({
  violation_id,
  currentUserId,
  member,
  violationCreatedAt,
}) {
  const permissions = useContext(PermissionsContext);
  const hasPermission = (perm) => permissions?.includes(perm);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [activeRow, setActiveRow] = useState(null);
  const [saving, setSaving] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  const showSnack = (msg, severity = "success") =>
    setSnack({ open: true, msg, severity });

  // Billing codes state
  const [expandedFine, setExpandedFine] = useState(null);
  const [fineBillingCodes, setFineBillingCodes] = useState({});
  const [newCode, setNewCode] = useState('');
  const [newCodeDesc, setNewCodeDesc] = useState('');
  const [loadingCodes, setLoadingCodes] = useState({});

  // Send to billing state
  const [sendBillingOpen, setSendBillingOpen] = useState(false);
  const [sendBillingFine, setSendBillingFine] = useState(null);
  const [sendBillingAmount, setSendBillingAmount] = useState('');
  const [sendBillingCode, setSendBillingCode] = useState('');
  const [sendBillingNotes, setSendBillingNotes] = useState('');
  const [sendingBilling, setSendingBilling] = useState(false);
  const [billingStatus, setBillingStatus] = useState({}); // fineId -> { count, records }

  const memberNumber =
    member?.ClubMemberCode ||
    member?.clubMemberCode ||
    member?.MemberNumber ||
    member?.member_number ||
    "";

  const ownerName = `${member?.FirstName || ""} ${member?.LastName || ""}`.trim();

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const fetchFines = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/violation-fines/violation/${violation_id}`);
      setRows(resp.data || []);
    } catch (e) {
      console.error("Error loading violation fines:", e);
      showSnack("Failed to load fines.", "error");
    } finally {
      setLoading(false);
    }
  }, [violation_id]);

  useEffect(() => {
    fetchFines();
  }, [fetchFines]);

  const totals = useMemo(() => {
    const total = rows.reduce((sum, r) => sum + Number(r.amount ?? 0), 0);
    return { count: rows.length, total };
  }, [rows]);

  // Billing codes handlers
  const fetchBillingCodes = async (fineId) => {
    setLoadingCodes(prev => ({ ...prev, [fineId]: true }));
    try {
      const res = await api.get(`/violation-fine-billing-codes/${fineId}`);
      setFineBillingCodes(prev => ({ ...prev, [fineId]: res.data }));
    } catch (e) { /* silent */ }
    finally { setLoadingCodes(prev => ({ ...prev, [fineId]: false })); }
  };

  const handleExpandFine = (fineId) => {
    const isExpanding = expandedFine !== fineId;
    setExpandedFine(isExpanding ? fineId : null);
    if (isExpanding && !fineBillingCodes[fineId]) fetchBillingCodes(fineId);
  };

  const handleAddBillingCode = async (fineId) => {
    if (!newCode.trim()) return;
    try {
      await api.post('/violation-fine-billing-codes',
        { violation_fine_id: fineId, item_group_code: newCode.trim(), description: newCodeDesc.trim() || null });
      setNewCode(''); setNewCodeDesc('');
      fetchBillingCodes(fineId);
    } catch (e) { showSnack('Failed to add billing code.', 'error'); }
  };

  const handleDeleteBillingCode = async (codeId, fineId) => {
    try {
      await api.delete(`/violation-fine-billing-codes/${codeId}`);
      fetchBillingCodes(fineId);
    } catch (e) { showSnack('Failed to delete billing code.', 'error'); }
  };

  const fetchBillingStatus = useCallback(async (fineId) => {
    try {
      const res = await api.get(`/violation-fines/${fineId}/billing-status`);
      setBillingStatus(prev => ({ ...prev, [fineId]: res.data }));
    } catch { /* non-blocking */ }
  }, []);

  const openSendToBilling = (fine) => {
    setSendBillingFine(fine);
    setSendBillingAmount(String(fine.amount || ''));
    setSendBillingCode('');
    setSendBillingNotes('');
    setSendBillingOpen(true);
    fetchBillingStatus(fine.id);
  };

  const handleSendToBilling = async () => {
    if (!sendBillingFine) return;
    setSendingBilling(true);
    try {
      const res = await api.post(`/violation-fines/${sendBillingFine.id}/send-to-billing`, {
        override_amount: sendBillingAmount !== '' ? Number(sendBillingAmount) : undefined,
        billing_code: sendBillingCode?.trim() || undefined,
        notes: sendBillingNotes?.trim() || undefined,
      });
      showSnack(`Sent to billing. Record #${res.data.billable_request_id}`, 'success');
      fetchBillingStatus(sendBillingFine.id);
      setSendBillingOpen(false);
      fetchFines();
    } catch (e) {
      showSnack(e.response?.data?.message || 'Failed to send to billing.', 'error');
    } finally {
      setSendingBilling(false);
    }
  };

  const openCreate = () => {
    setDialogMode("create");
    setActiveRow({
      violation_id: Number(violation_id),
      member_number: memberNumber || null,
      title: ownerName ? `Violation Fine - ${ownerName}` : "Violation Fine",
      description: "",
      amount: 0,
      created_by: currentUserId ?? null,
      effective_date: violationCreatedAt || new Date().toISOString(),
      is_recurring: false,
      recurrence_frequency: "monthly",
      recurrence_interval: 1,
      recurrence_end_date: null,
      is_active: true,
      next_run_at: null,
      last_run_at: null,
    });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setDialogMode("edit");
    setActiveRow({
      ...row,
      member_number: row.member_number ?? memberNumber ?? null,
      created_by: row.created_by ?? currentUserId ?? null,
    });
    setDialogOpen(true);
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (dialogMode === "create") {
        await api.post("/violation-fines", payload);
        showSnack("Fine rule created.", "success");
      } else {
        await api.put(`/violation-fines/${activeRow.id}`, payload);
        showSnack("Fine rule updated.", "success");
      }

      setDialogOpen(false);
      await fetchFines();
    } catch (e) {
      console.error("Save violation fine error:", e);
      showSnack("Failed to save fine rule.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 2.25,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={2}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <PaidIcon sx={{ color: "#2f63ad" }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                  Fines
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Create one-time or recurring fine rules. Recurring fines generate billable
                records via cron.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title="Refresh">
                <IconButton
                  onClick={fetchFines}
                  disabled={loading}
                  sx={{
                    border: "1px solid rgba(47,99,173,0.12)",
                    borderRadius: "10px",
                    backgroundColor: "rgba(47,99,173,0.03)",
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>

              {hasPermission("manage_violation_fines") && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreate}
                  sx={primaryButtonSx}
                >
                  New Fine
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>

        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2.25 }}>
            <Chip
              label={`${totals.count} rule(s)`}
              sx={{ borderRadius: "999px", fontWeight: 700 }}
            />
            <Chip
              label={`Total Amount: ${fmtCurrency(totals.total)}`}
              sx={{ borderRadius: "999px", fontWeight: 700 }}
            />
            {memberNumber ? (
              <Chip
                label={`Member: ${memberNumber}`}
                sx={{ borderRadius: "999px", fontWeight: 700 }}
              />
            ) : null}
          </Stack>

          <Divider sx={{ mb: 2.25, borderColor: "rgba(47,99,173,0.08)" }} />

          {loading ? (
            <Box
              sx={{
                minHeight: "180px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Card sx={{ ...pageCardSx, p: 3, textAlign: "center", boxShadow: "none" }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
                No fines yet
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
                Click <b>New Fine</b> to create the first fine rule.
              </Typography>
            </Card>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table
                size="small"
                sx={{
                  minWidth: 760,
                  "& .MuiTableCell-head": {
                    fontWeight: 700,
                    color: "#223142",
                    backgroundColor: "#f7f9fc",
                    borderBottom: "1px solid rgba(47,99,173,0.08)",
                  },
                  "& .MuiTableCell-body": {
                    color: "#223142",
                    borderBottom: "1px solid rgba(47,99,173,0.06)",
                  },
                  "& .MuiTableRow-root:hover": {
                    backgroundColor: "rgba(47,99,173,0.03)",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Effective</TableCell>
                    <TableCell>Recurring</TableCell>
                    <TableCell>Next Run</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((r) => (
                    <React.Fragment key={r.id}>
                      <TableRow hover>
                        <TableCell sx={{ maxWidth: 340 }}>
                          <Typography sx={{ fontWeight: 600 }} noWrap title={r.title}>
                            {r.title}
                          </Typography>
                          {r.description ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              title={r.description}
                            >
                              {r.description}
                            </Typography>
                          ) : null}
                        </TableCell>

                        <TableCell>{fmtCurrency(r.amount)}</TableCell>
                        <TableCell>{fmtDateTime(r.effective_date)}</TableCell>

                        <TableCell>
                          {r.is_recurring ? (
                            <Chip
                              size="small"
                              label={`${r.recurrence_frequency} × ${
                                r.recurrence_interval || 1
                              }`}
                              sx={{ borderRadius: "999px", fontWeight: 700 }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="One-time"
                              sx={{ borderRadius: "999px", fontWeight: 700 }}
                            />
                          )}
                        </TableCell>

                        <TableCell>{fmtDateTime(r.next_run_at)}</TableCell>
                        <TableCell>{r.is_active ? "Yes" : "No"}</TableCell>

                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                            {/* Billing status badge */}
                            {r.last_billable_request_id && (
                              <Chip size="small" icon={<PaidIcon sx={{ fontSize:14 }} />}
                                label="Billed"
                                sx={{ bgcolor:"rgba(34,197,94,0.12)", color:"#16a34a", fontWeight:700, fontSize:"0.7rem" }} />
                            )}

                            {hasPermission("manage_violation_fines") && (
                              <Button size="small" variant="outlined" startIcon={<EditIcon />}
                                onClick={() => openEdit(r)} sx={secondaryButtonSx}>
                                Edit
                              </Button>
                            )}

                            <Button size="small" variant="contained"
                              startIcon={<PaidIcon sx={{ fontSize:14 }} />}
                              onClick={() => openSendToBilling(r)}
                              sx={{ bgcolor:"#16a34a", textTransform:"none", fontSize:"0.75rem", borderRadius:"8px", fontWeight:700, boxShadow:"none",
                                "&:hover":{ bgcolor:"#15803d", boxShadow:"none" } }}>
                              Send to Billing
                            </Button>

                            <Button size="small"
                              startIcon={expandedFine === r.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              onClick={() => handleExpandFine(r.id)}
                              sx={{ color:'#2f63ad', textTransform:'none', fontSize:'0.78rem', borderRadius:'10px' }}>
                              Codes {fineBillingCodes[r.id]?.length ? `(${fineBillingCodes[r.id].length})` : ''}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>

                      {/* Billing codes expandable row */}
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 0, border: expandedFine === r.id ? undefined : 'none' }}>
                          <Collapse in={expandedFine === r.id} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 1, mb: 1.5, p: 1.5, bgcolor: '#f5f7fa', borderRadius: 2 }}>
                              {loadingCodes[r.id] ? (
                                <CircularProgress size={18} />
                              ) : (
                                <>
                                  {(fineBillingCodes[r.id] || []).map(code => (
                                    <Box key={code.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                      <Chip label={code.item_group_code} size="small" sx={{ bgcolor: '#e3ecf7', color: '#2f63ad' }} />
                                      {code.description && (
                                        <Typography variant="caption" color="text.secondary">{code.description}</Typography>
                                      )}
                                      <IconButton size="small" onClick={() => handleDeleteBillingCode(code.id, r.id)}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  ))}
                                  {(fineBillingCodes[r.id] || []).length === 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                      No billing codes assigned.
                                    </Typography>
                                  )}
                                  {/* Add new code inline form */}
                                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <TextField
                                      size="small"
                                      label="Billing Code"
                                      value={newCode}
                                      onChange={e => setNewCode(e.target.value)}
                                      sx={{ width: 130 }}
                                      placeholder="e.g. FINE01"
                                    />
                                    <TextField
                                      size="small"
                                      label="Description (optional)"
                                      value={newCodeDesc}
                                      onChange={e => setNewCodeDesc(e.target.value)}
                                      sx={{ flex: 1, minWidth: 160 }}
                                    />
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => handleAddBillingCode(r.id)}
                                      disabled={!newCode.trim()}
                                      sx={{ borderRadius: '8px', textTransform: 'none' }}
                                    >
                                      Add
                                    </Button>
                                  </Box>
                                </>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <FineDialog
        open={dialogOpen}
        mode={dialogMode}
        initial={activeRow || {}}
        onClose={() => !saving && setDialogOpen(false)}
        onSave={handleSave}
        isSaving={saving}
      />

      {/* ── Send to Billing Dialog ─────────────────────────────────────── */}
      <Dialog open={sendBillingOpen} onClose={() => setSendBillingOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx:{ borderRadius:"16px" } }}>
        <DialogTitle sx={{ fontWeight:700, color:"#223142", display:"flex", alignItems:"center", gap:1 }}>
          <PaidIcon sx={{ color:"#16a34a" }} /> Send Fine to Billing
        </DialogTitle>
        <DialogContent>
          {sendBillingFine && (
            <>
              <Alert severity="info" sx={{ mb:2, borderRadius:2 }}>
                Sending <strong>{sendBillingFine.title}</strong> to the billing queue. This will create a record in Billable Requests for export to the accounting system.
              </Alert>

              {/* Previous billing records */}
              {billingStatus[sendBillingFine.id]?.count > 0 && (
                <Alert severity="warning" sx={{ mb:2, borderRadius:2 }}>
                  This fine has already been billed {billingStatus[sendBillingFine.id].count} time(s). A new billing record will be created.
                </Alert>
              )}

              <TextField fullWidth label="Amount ($)" type="number" inputProps={{ min:0, step:0.01 }}
                value={sendBillingAmount} onChange={e=>setSendBillingAmount(e.target.value)}
                sx={{ mb:2, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />

              <TextField fullWidth label="Billing Code (Item Group)" value={sendBillingCode}
                onChange={e=>setSendBillingCode(e.target.value)}
                helperText="Jonas or accounting system item code. Leave blank to use system default."
                sx={{ mb:2, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />

              <TextField fullWidth label="Notes" multiline rows={2} value={sendBillingNotes}
                onChange={e=>setSendBillingNotes(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px:3, pb:3, gap:1 }}>
          <Button onClick={() => setSendBillingOpen(false)} variant="outlined"
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>
          <Button onClick={handleSendToBilling} variant="contained" disabled={sendingBilling}
            sx={{ bgcolor:"#16a34a", borderRadius:"10px", textTransform:"none", fontWeight:700,
              boxShadow:"none", "&:hover":{ bgcolor:"#15803d", boxShadow:"none" } }}>
            {sendingBilling ? <CircularProgress size={20} color="inherit" /> : "Send to Billing"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((p) => ({ ...p, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 3 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}